import React, { use, useMemo, useState } from 'react';
import {
    buildTrendsChartData,
    extractCategoryOptions,
    getXAxisTitle,
    defaultPeriod,
    defaultUnit,
    unitOptions,
    periodArray,
    RiskTrendPeriod,
} from './risk-trends.utils';
import { BasicDropDown } from '@components-clean/core/basic-dropdown';
import chartStyles from '@components-clean/core/charts/shared/style.module.scss';
import PanelFrame from '@components-clean/core/panel-frame';
import { RiskDashboardContext } from '@pages-clean/risk-assessment/risk-dashboard/context';
import { PanelType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import Constants from '@utils/constants';

interface FilterItem {
    id: string | null;
    text: string;
}

const unitItems: FilterItem[] = unitOptions.map((u) => ({ id: `${u}`, text: `${u}` }));

const periodItems: FilterItem[] = periodArray.map((text: string, idx: number) => ({ id: `${idx}`, text }));

const RiskTrendsChart = () => {
    const { t } = useTranslation('ram');
    const { risks } = use(RiskDashboardContext);

    const [periodType, setPeriodType] = useState<RiskTrendPeriod>(defaultPeriod);
    const [unit, setUnit] = useState(defaultUnit);
    const [categoryId, setCategoryId] = useState<string | null>(null);

    const categoryOptions = useMemo(() => extractCategoryOptions(risks), [risks]);

    const chartData = useMemo(
        () => buildTrendsChartData(risks, periodType, unit, categoryId),
        [risks, periodType, unit, categoryId],
    );

    const hasEvaluatedRisks = useMemo(() => risks.some((r) => (r.evaluationHistory?.length ?? 0) > 0), [risks]);

    const categoryItems = useMemo<FilterItem[]>(
        () => [
            { id: null, text: t('riskDashboard.riskTrends.filters.allCategories') },
            { id: Constants.ObjectId.Empty, text: t('riskDashboard.riskTrends.filters.unassigned') },
            ...categoryOptions.map((c) => ({ id: c.id, text: c.name })),
        ],
        [categoryOptions, t],
    );

    return (
        <PanelFrame
            title={t('riskDashboard.riskTrends.title')}
            panelType={PanelType.GRAPH}
            graphType="STACKEDCOLUMN"
            panelData={chartData}
            hidePanelSetup
            showLegend={false}
            nonFilterable
            xAxisTitle={t(getXAxisTitle(periodType))}
            yAxisTitle={t('riskDashboard.riskTrends.yAxisTitle')}
            customOptions={{
                colors: [
                    chartStyles['rag-colour-red'],
                    chartStyles['rag-colour-amber'],
                    chartStyles['rag-colour-green'],
                ],
                plotOptions: {
                    column: { stacking: 'percent' },
                    bar: { stacking: 'percent' },
                },
            }}
            preChartContent={
                hasEvaluatedRisks ? (
                    <div className="k-display-flex k-gap-4 k-pb-4 k-align-items-end">
                        <div style={{ width: 80, flexShrink: 0 }}>
                            <BasicDropDown
                                data-testid="risk-trends-unit-filter"
                                label={t('riskDashboard.riskTrends.filters.unit')}
                                data={unitItems}
                                textField="text"
                                dataItemKey="id"
                                value={unitItems.find((i) => i.id === `${unit}`) ?? null}
                                onChange={(e) => setUnit(Number((e.value as FilterItem)?.id ?? defaultUnit))}
                            />
                        </div>
                        <div style={{ width: 140, flexShrink: 0 }}>
                            <BasicDropDown
                                data-testid="risk-trends-period-filter"
                                label={t('riskDashboard.riskTrends.filters.period')}
                                data={periodItems}
                                textField="text"
                                dataItemKey="id"
                                value={periodItems.find((i) => i.id === `${periodType}`) ?? null}
                                onChange={(e) =>
                                    setPeriodType(
                                        Number((e.value as FilterItem)?.id ?? defaultPeriod) as RiskTrendPeriod,
                                    )
                                }
                            />
                        </div>
                        <div style={{ width: 280, flexShrink: 0 }}>
                            <BasicDropDown
                                data-testid="risk-trends-category-filter"
                                label={t('riskDashboard.riskTrends.filters.category')}
                                data={categoryItems}
                                textField="text"
                                dataItemKey="id"
                                value={categoryItems.find((i) => i.id === categoryId) ?? categoryItems[0]}
                                onChange={(e) => setCategoryId((e.value as FilterItem)?.id ?? null)}
                            />
                        </div>
                    </div>
                ) : undefined
            }
            data-testid="risk-trends-chart"
            version={0}
        />
    );
};

export default RiskTrendsChart;
